import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";
import { translate } from "src/app/i18next";
import { Period, TimeRange } from "./util/time-range";

@Component({
	selector: "app-time-picker",
	templateUrl: "./time-picker.component.html",
	styleUrls: ["./time-picker.component.scss"],
})
export class TimePickerComponent implements OnChanges {
	@Input() label?: string;
	@Input() showNotifier = false;
	@Input() resetTimeWhenMeridiemChanges = false;

	/** "HH:MM AM/PM" or empty/"00:00 AM" sentinel for blank */
	@Input() value?: string;

	/** Optional coarse constraints (independent lists). */
	@Input() timeRange?: TimeRange;

	/** Disable interaction & show disabled look. Defaults to false. */
	@Input() disabled = false;

	/** Strict lower bound (exclusive), e.g. "08:00 AM". */
	@Input() after?: string;

	@Output() timeChange = new EventEmitter<string>();

	// Default option sets
	private readonly defaultHours = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
	private readonly defaultMinutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0..55 by 5
	private readonly defaultPeriods: Period[] = ["AM", "PM"];

	// Option arrays used by template (overridden by timeRange)
	hours: number[] = [...this.defaultHours];
	minutes: number[] = [...this.defaultMinutes];
	periods: Period[] = [...this.defaultPeriods];

	// Current selection (start blank)
	selectedHour: number | null = null;
	selectedMinute: number | null = null;
	selectedPeriod: Period | null = null;

	// Whether the control currently has a real value
	hasValue = false;

	open = false;

	tooltipMessage = translate("inspection after midnight tooltip");

	constructor(private eRef: ElementRef) {}

	// ---------------- utils ----------------

	private isEmptySentinel(v?: string | null): boolean {
		if (!v) return true; // "", null, undefined
		const s = v.trim().toUpperCase();
		return s === "00:00" || s === "00:00 AM" || s === "00:00 PM";
	}

	/** Sort hours with 12 first, then 1..11 ascending. */
	private sortHours12First(list: number[]): number[] {
		return [...list].sort((a, b) => {
			if (a === 12 && b !== 12) return -1;
			if (b === 12 && a !== 12) return 1;
			return a - b;
		});
	}

	private applyTimeRange() {
		this.hours = this.timeRange?.hours?.length ? [...this.timeRange.hours] : [...this.defaultHours];
		this.minutes = this.timeRange?.minutes?.length ? [...this.timeRange.minutes] : [...this.defaultMinutes];
		this.periods = this.timeRange?.periods?.length
			? ([...this.timeRange.periods] as Period[])
			: [...this.defaultPeriods];

		this.hours = this.sortHours12First(this.hours);
		this.minutes.sort((a, b) => a - b);
		this.periods = this.periods.filter(p => p === "AM" || p === "PM");
		if (!this.periods.length) this.periods = [...this.defaultPeriods];
	}

	private coerceSelectionIntoRange() {
		if (this.selectedHour != null && !this.hours.includes(this.selectedHour)) {
			this.selectedHour = this.hours[0] ?? null;
		}
		if (this.selectedMinute != null && !this.minutes.includes(this.selectedMinute)) {
			this.selectedMinute = this.minutes[0] ?? null;
		}
		if (this.selectedPeriod != null && !this.periods.includes(this.selectedPeriod)) {
			this.selectedPeriod = this.periods[0] ?? null;
		}
	}

	private toMinutes(h12: number, m: number, period: Period): number {
		let h24 = h12 % 12;
		if (period === "PM") h24 += 12;
		return h24 * 60 + m;
	}

	private parseToMinutes(v?: string | null): number | null {
		if (this.isEmptySentinel(v)) return null;
		const [t, pRaw] = (v ?? "").trim().split(" ");
		const [hh, mm] = (t ?? "").split(":");
		const h = parseInt(hh || "", 10);
		const m = parseInt(mm || "", 10);
		const p = (pRaw || "AM").toUpperCase() as Period;
		if (Number.isNaN(h) || Number.isNaN(m) || (p !== "AM" && p !== "PM")) return null;
		return this.toMinutes(h, m, p);
	}

	// ---------------- dynamic option lists honoring `min` ----------------

	/** Hours available given `timeRange`, `selectedPeriod`, and `min` (exclusive). */
	get availableHours(): number[] {
		let base = this.hours;

		if (this.selectedPeriod && this.after) {
			const afterMin = this.parseToMinutes(this.after) + 1 * 60; // 1 hour later
			if (afterMin != null) {
				base = base.filter(h => {
					const smallestMinute = this.minutes[0] ?? 0;
					const largestMinute = this.minutes[this.minutes.length - 1] ?? 55;
					if (this.toMinutes(h, smallestMinute, this.selectedPeriod!) > afterMin) return true;
					return this.toMinutes(h, largestMinute, this.selectedPeriod!) > afterMin;
				});
			}
		}

		return this.sortHours12First(base);
	}

	/** Minutes available given selectedHour/Period and `min` (exclusive). */
	get availableMinutes(): number[] {
		if (!this.selectedHour || !this.selectedPeriod) return this.minutes;
		const minMin = this.parseToMinutes(this.after);
		if (minMin == null) return this.minutes;
		return this.minutes.filter(m => this.toMinutes(this.selectedHour!, m, this.selectedPeriod!) > minMin);
	}

	// ---------------- display ----------------

	get displayTime(): string {
		if (!this.hasValue || this.selectedHour == null || this.selectedMinute == null || !this.selectedPeriod) {
			return "";
		}
		const hh = this.selectedHour.toString().padStart(2, "0");
		const mm = this.selectedMinute.toString().padStart(2, "0");
		return `${hh}:${mm} ${this.selectedPeriod}`;
	}

	// ---------------- lifecycle ----------------

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["timeRange"]) {
			this.applyTimeRange();
			if (this.hasValue) this.coerceSelectionIntoRange();
		}

		if (changes["value"]) {
			if (this.isEmptySentinel(this.value)) {
				// Treat sentinel/empty as "no selection"
				this.hasValue = false;
				this.selectedHour = null;
				this.selectedMinute = null;
				this.selectedPeriod = null;
				return;
			}

			// Parse "HH:MM AM/PM"
			const [t, p] = (this.value || "").split(" ");
			const [hh, mm] = (t || "").split(":");
			const h = parseInt(hh || "", 10);
			const m = parseInt(mm || "", 10);
			const per = (p || "AM").toUpperCase() as Period;

			if (!Number.isNaN(h)) this.selectedHour = h;
			if (!Number.isNaN(m)) this.selectedMinute = m;
			if (per === "AM" || per === "PM") this.selectedPeriod = per;

			this.hasValue = true;
			this.coerceSelectionIntoRange();

			// remove AM if after >= 11am
			const isAtOrAfter = (t: string, h: number) => {
				const [time, meridian] = t.split(" ");
				let [hours, minutes] = time.split(":").map(Number);
				if (meridian === "PM" && hours !== 12) hours += 12;
				if (meridian === "AM" && hours === 12) hours = 0;
				return hours + minutes / 60 >= h;
			};
			if (this.after && isAtOrAfter(this.after, 11)) {
				this.periods = ["PM"];
			} else {
				this.periods = ["AM", "PM"];
			}
		}
	}

	// ---------------- interactions ----------------

	/** Called when any part (hour/minute/period) changes via the selects. */
	onPartChanged(_part: "hour" | "minute" | "period") {
		if (this.disabled) return;

		if (this.resetTimeWhenMeridiemChanges && _part === "period") {
			this.selectedHour = null;
			this.selectedMinute = null;
			this.hasValue = false;
			return;
		}

		// If user selected one part while others are still blank, auto-fill them
		// with the first valid options so we establish a real value and drop the
		// placeholder options from the lists.
		if (this.selectedPeriod == null) {
			this.selectedPeriod = this.periods[0] ?? "AM";
		}
		if (this.selectedHour == null) {
			const firstHour = this.availableHours[0] ?? this.hours[0] ?? 12;
			this.selectedHour = firstHour;
		}
		if (this.selectedMinute == null) {
			this.selectedMinute = this.availableMinutes[0] ?? this.minutes[0] ?? 0;
		}

		// Now we definitely have a value
		this.hasValue = true;

		// Emit full value
		this.timeChange.emit(this.displayTime);
	}

	toggleOpen() {
		if (this.disabled) return;
		this.open = !this.open;
	}

	@HostListener("focusout", ["$event"])
	onFocusOut(event: FocusEvent) {
		if (this.open && !this.eRef.nativeElement.contains(event.relatedTarget as Node)) {
			this.open = false;
		}
	}

	@HostListener("document:click", ["$event"])
	onClickOutside(event: MouseEvent) {
		if (this.open && !this.eRef.nativeElement.contains(event.target)) {
			this.open = false;
		}
	}
}
